import { Injectable } from '@angular/core';
import { RuntimeConfigService } from '../config/runtime-config.service';
import { LocalDatabaseService } from '../storage/local-database.service';
import { LoggerService } from '../utils/logger.service';

export interface OfflineEntitlementClaims {
  iss: string;
  aud: string | string[];
  sub: string;
  iat: number;
  exp: number;
  courses: Record<string, unknown>[];
  subscriptions: Record<string, unknown>[];
}

@Injectable({ providedIn: 'root' })
export class LocalEntitlementService {
  constructor(
    private localDb: LocalDatabaseService,
    private runtime: RuntimeConfigService,
  ) {}

  async saveVerifiedToken(userId: number, token: string): Promise<boolean> {
    const claims = await this.verifyToken(userId, token);
    if (!claims || !this.localDb.isNative) return false;
    await this.localDb.saveOfflineEntitlement(
      userId,
      token,
      new Date(claims.exp * 1000).toISOString(),
    );
    return true;
  }

  async loadCurrentClaims(userId = this.currentUserId()): Promise<OfflineEntitlementClaims | null> {
    if (!userId || !this.localDb.isNative) return null;
    try {
      const stored = await this.localDb.loadOfflineEntitlement(userId);
      if (!stored || Date.parse(stored.expiresAt) <= Date.now()) return null;
      return await this.verifyToken(userId, stored.token);
    } catch (error) {
      LoggerService.error('[offline-entitlement] Could not load entitlement lease', error);
      return null;
    }
  }

  async getCourses(): Promise<Record<string, unknown>[] | null> {
    const claims = await this.loadCurrentClaims();
    if (!claims) return null;
    const subscriptionsByCourse = new Map<number, Record<string, unknown>[]>();
    for (const subscription of claims.subscriptions ?? []) {
      const courseId = Number(subscription['courseId']);
      if (!Number.isFinite(courseId)) continue;
      const rows = subscriptionsByCourse.get(courseId) ?? [];
      rows.push(subscription);
      subscriptionsByCourse.set(courseId, rows);
    }
    return (claims.courses ?? []).filter((course) => {
      const courseId = Number(course['id'] ?? course['courseId']);
      const rows = subscriptionsByCourse.get(courseId);
      // Starting a trial requires the server so its start time and usage limits
      // remain authoritative. Offline access requires an existing active row.
      if (!rows?.length) return false;
      return rows.some((row) => this.subscriptionIsCurrent(row));
    });
  }

  async getSubscriptions(): Promise<Record<string, unknown>[] | null> {
    const claims = await this.loadCurrentClaims();
    return claims?.subscriptions ?? null;
  }

  async clear(userId?: number): Promise<void> {
    if (!this.localDb.isNative) return;
    await this.localDb.clearOfflineEntitlements(userId);
  }

  private subscriptionIsCurrent(subscription: Record<string, unknown>): boolean {
    if (String(subscription['trialStatus'] ?? '').toLowerCase() === 'expired') return false;
    const status = String(subscription['status'] ?? '').toLowerCase();
    if (status && status !== 'active') return false;
    const validTo = String(subscription['validTo'] ?? '').trim();
    if (!validTo) return status === 'active';
    const end = Date.parse(validTo.length === 10 ? `${validTo}T23:59:59.999Z` : validTo);
    return Number.isFinite(end) && end >= Date.now();
  }

  private async verifyToken(userId: number, token: string): Promise<OfflineEntitlementClaims | null> {
    try {
      const publicKey = this.runtime.offlineEntitlementPublicKey;
      const parts = token.split('.');
      if (!publicKey || parts.length !== 3) return null;
      const header = JSON.parse(this.decodeText(parts[0])) as { alg?: string };
      if (header.alg !== 'RS256') return null;
      const key = await crypto.subtle.importKey(
        'spki',
        this.decodeBytes(publicKey),
        { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
        false,
        ['verify'],
      );
      const valid = await crypto.subtle.verify(
        'RSASSA-PKCS1-v1_5',
        key,
        this.decodeBytes(parts[2]),
        new TextEncoder().encode(`${parts[0]}.${parts[1]}`),
      );
      if (!valid) return null;
      const claims = JSON.parse(this.decodeText(parts[1])) as OfflineEntitlementClaims;
      const audience = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
      const now = Math.floor(Date.now() / 1000);
      if (
        claims.iss !== 'https://api.atplane.com'
        || !audience.includes('atplane-offline')
        || claims.sub !== String(userId)
        || !Number.isFinite(claims.exp)
        || claims.exp <= now
        || claims.iat > now + 300
        || !Array.isArray(claims.courses)
        || !Array.isArray(claims.subscriptions)
      ) {
        return null;
      }
      return claims;
    } catch (error) {
      LoggerService.error('[offline-entitlement] Lease verification failed', error);
      return null;
    }
  }

  private currentUserId(): number | null {
    try {
      const stored = localStorage.getItem('user');
      if (!stored) return null;
      const raw = JSON.parse(stored) as Record<string, unknown>;
      const id = Number(raw['_id']);
      return Number.isFinite(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  }

  private decodeText(value: string): string {
    return new TextDecoder().decode(this.decodeBytes(value));
  }

  private decodeBytes(value: string): Uint8Array {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const binary = atob(padded);
    return Uint8Array.from(binary, (char) => char.charCodeAt(0));
  }
}
